import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInfiniteFeed } from "@/api/useInfiniteFeed";
import type { FeedFilters } from "@/api/types";
import { officialFeesApi, type SimilarFeesQuery } from "../api/officialFees";
import type { OfficialFeeRequestFormData, OfficialFeeRequestList } from "../types";

const QK = "official-fee-requests";

export function useSimilarOfficialFees(params: SimilarFeesQuery) {
  const enabled = Boolean(
    params.office_name &&
      params.office_account_number &&
      params.amount &&
      params.paying_company,
  );
  return useQuery({
    queryKey: [QK, "similar", params],
    queryFn: () => officialFeesApi.similar(params),
    enabled,
    staleTime: 30_000,
  });
}

/** Whole collection in one response — kept for AI/ARON callers. The list page uses
 *  useOfficialFeeRequestsFeed instead. */
export function useOfficialFeeRequests(params?: Record<string, string>) {
  return useQuery({
    queryKey: [QK, params],
    queryFn: () => officialFeesApi.list(params),
    staleTime: 0,
  });
}

export function useOfficialFeeRequestsFeed(filters: FeedFilters) {
  return useInfiniteFeed<OfficialFeeRequestList>({
    // Prefixed with QK so the mutations below, which invalidate [QK], also refresh the
    // feed — invalidateQueries matches by prefix.
    key: [QK, "feed"],
    url: "/official-fee-requests/feed/",
    filters,
    // Approvals must not read stale for a minute (the global default), same as the
    // list query above.
    staleTime: 0,
  });
}

export function useOfficialFeeRequest(id: string | undefined) {
  return useQuery({
    queryKey: [QK, id],
    queryFn: () => officialFeesApi.get(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

/**
 * Monthly sums under the list (technical director / CEO). Keyed under QK so every
 * approval, payment and withdrawal below invalidates it together with the list —
 * a total that lags a row the person just approved would be read as a wrong number.
 */
export function useOfficialFeeMonthlyTotals(
  filters: Record<string, string | undefined>,
  /** Statusy z filtra listy — sumy muszą opisywać te same wiersze, które widać. */
  statuses?: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: [QK, "monthly-totals", filters, statuses ?? null],
    queryFn: () => officialFeesApi.monthlyTotals(filters, statuses),
    enabled,
    staleTime: 0,
  });
}

export function usePendingAcknowledgementCount(enabled = true) {
  return useQuery({
    queryKey: [QK, "pending-acknowledgement-count"],
    queryFn: () => officialFeesApi.pendingAcknowledgementCount(),
    enabled,
    refetchInterval: 60_000,
  });
}

export function usePendingApprovalCount(enabled = true) {
  return useQuery({
    queryKey: [QK, "pending-approval-count"],
    queryFn: () => officialFeesApi.pendingApprovalCount(),
    enabled,
    refetchInterval: 60_000,
  });
}

export function usePendingPaymentCount(enabled = true) {
  return useQuery({
    queryKey: [QK, "pending-payment-count"],
    queryFn: () => officialFeesApi.pendingPaymentCount(),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAddOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      paymentFiles,
      attachmentFiles,
    }: {
      data: OfficialFeeRequestFormData;
      paymentFiles: File[];
      attachmentFiles: File[];
    }) => officialFeesApi.addFee(data, paymentFiles, attachmentFiles),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useCreateOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OfficialFeeRequestFormData) => officialFeesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useUpdateOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OfficialFeeRequestFormData> }) =>
      officialFeesApi.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
    },
  });
}

export function useDeleteOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useApproveOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.approve(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-approval-count"] });
    },
  });
}

export function useRejectOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejection_comment }: { id: string; rejection_comment: string }) =>
      officialFeesApi.reject(id, rejection_comment),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-approval-count"] });
    },
  });
}

export function useUploadOfficialFeePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      officialFeesApi.uploadPayment(id, files),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-acknowledgement-count"] });
      qc.invalidateQueries({ queryKey: [QK, "pending-payment-count"] });
    },
  });
}

export function useAcknowledgeOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.acknowledge(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-acknowledgement-count"] });
    },
  });
}

export function useWithdrawOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.withdraw(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-approval-count"] });
    },
  });
}

/**
 * Wstrzymanie i przywrócenie — obie zmieniają liczbę prośb czekających na akceptację
 * (wstrzymana wypada z kolejki, przywrócona do niej wraca), więc unieważniają ten sam
 * zestaw kluczy co akceptacja.
 */
export function useHoldOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.hold(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-approval-count"] });
    },
  });
}

export function useRestoreOfficialFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.restore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, "pending-approval-count"] });
    },
  });
}

export function useOfficialFeeHistory(id: string | null) {
  return useQuery({
    queryKey: [QK, id, "history"],
    queryFn: () => officialFeesApi.history(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCloneOfficialFee() {
  return useMutation({
    mutationFn: (id: string) => officialFeesApi.clone(id),
  });
}

export function useUploadOfficialFeeAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) =>
      officialFeesApi.uploadAttachments(id, files),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, id, "history"] });
    },
  });
}

export function useDeleteOfficialFeeAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) =>
      officialFeesApi.deleteAttachment(id, fileId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [QK] });
      qc.invalidateQueries({ queryKey: [QK, id] });
      qc.invalidateQueries({ queryKey: [QK, id, "history"] });
    },
  });
}
